import {
  Button,
  Box,
  Text,
  TextArea,
  IconButton,
  Popover,
  Heading,
  Flex,
  Select,
} from "@radix-ui/themes";
import { useState, useRef, useCallback, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { getIcon } from "@utils/jsxHelpers";
import { recordResponse } from "@api/survey";
import { ChatBubbleIcon } from "@radix-ui/react-icons";
import PropTypes from "prop-types";
import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useSurveyStore } from "@src/store/survey";

Comment.propTypes = {
  question: PropTypes.object,
  marker: PropTypes.object,
  rootRef: PropTypes.object,
  sessionUUID: PropTypes.string,
  hoverFeature: PropTypes.object,
};

SelectQuestion.propTypes = {
  question: PropTypes.object,
  subquestionResponse: PropTypes.object,
  setSubquestionResponse: PropTypes.func,
};

function SelectQuestion({
  question,
  subquestionResponse,
  setSubquestionResponse,
}) {
  const [selected, setSelected] = useState();

  if (question !== undefined) {
    return (
      <Flex direction="column" width="100%" mb="2">
        <Text as="p" size="2">
          {question.question}
        </Text>
        <Text as="p" size="1" color="gray" mb="2">
          {question.hint_text}
        </Text>
        <Select.Root
          defaultValue={
            subquestionResponse[question.question]
              ? subquestionResponse[question.question]
              : -1
          }
          onValueChange={(value) => {
            setSelected(value);
            setSubquestionResponse({
              ...subquestionResponse,
              [question.question]: value,
            });
          }}
          value={selected}
        >
          <Select.Trigger />
          <Select.Content>
            <Select.Group>
              <Select.Item value={-1} key={-1}>
                {question.options.default !== undefined ? (
                  question.options.default
                ) : (
                  <Trans id="question.select_option">Select an option</Trans>
                )}
              </Select.Item>
              {question.options.options.map((option, idx) => (
                <Select.Item value={option.label} key={idx}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Group>
          </Select.Content>
        </Select.Root>
      </Flex>
    );
  } else {
    return null;
  }
}

export default function Comment({
  marker,
  rootRef,
  question,
  sessionUUID,
  hoverFeature,
}) {
  const [isVisible, setVisible] = useState(true);
  const [answering, setAnswering] = useState(true);
  const [comment, setComment] = useState("");
  const [savedComment, setSavedComment] = useState("");
  const [errorMessage, setErrorMessage] = useState();
  const [subquestionResponse, setSubquestionResponse] = useState({});
  const [featureName, setFeatureName] = useState("");
  const uuid = useRef(null);
  const { i18n } = useLingui();
  const mapRef = useSurveyStore((state) => state.getMapRef());
  const response = useMutation({
    mutationFn: recordResponse,
    retry: 1,
    retryDelay: 1000,
    onError: (error) => {
      setErrorMessage(error.message.toString());
    },
    onSuccess: (data) => {
      setSavedComment(data.response.comment);
    },
    onSettled: () => {
      setAnswering(false);
      setVisible(false);
    },
  });

  useEffect(() => {
    uuid.current = self.crypto.randomUUID();
  }, []);

  useEffect(() => {
    const featureName =
      hoverFeature?.source === "mps_schools"
        ? hoverFeature?.properties?.school_name
        : hoverFeature?.properties?.Cluster;
    if (!featureName) return;
    setFeatureName(featureName);
  }, [hoverFeature]);

  function formatComment(comment) {
    return comment.length > 100 ? comment.substring(0, 100) + "..." : comment;
  }

  async function handleSaveComment() {
    if (comment.length > 0) {
      let responseData = {
        comment: comment,
        location: marker.getLngLat().toArray(),
        feature: hoverFeature?.properties,
      };

      if (Object.keys(subquestionResponse).length > 0) {
        responseData["subquestion_responses"] = subquestionResponse;
      }

      response.mutate({
        answerUUID: uuid.current,
        response: responseData,
        sessionUUID: sessionUUID,
        surveyQuestionUUID: question.uuid,
      });
    }
  }

  const handleCancelComment = useCallback(() => {
    if (savedComment.length == 0) {
      setTimeout(() => marker.remove(), 50);
    }
    setAnswering(false);
    setVisible(false);
  }, [savedComment, marker, setVisible, rootRef]);

  function handleCommentChange(e) {
    // Once a user has started to type a comment, persist it
    if (rootRef.current) {
      rootRef.current = null;
    }
    if (e.target.value.length < 1000) {
      setComment(e.target.value);
      setErrorMessage();
    } else {
      setErrorMessage(
        <Trans id="questions.Comment.too_long">
          Comment cannot exceed 1,000 characters
        </Trans>,
      );
    }
  }

  const escFunction = useCallback(
    (event) => {
      if (event.key === "Escape") {
        handleCancelComment();
      }
    },
    [handleCancelComment],
  );

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false);

    return () => {
      document.removeEventListener("keydown", escFunction, false);
    };
  }, [escFunction]);

  if (!mapRef) return;

  return (
    <Popover.Root open={isVisible}>
      <Popover.Trigger>
        <IconButton
          onClick={() => setVisible(!isVisible)}
          variant="solid"
          color={question?.options?.color ? question.options.color : "gray"}
          size="3"
        >
          {question === null ? (
            <ChatBubbleIcon width="32" height="32" />
          ) : (
            getIcon(question.options.icon, { width: 32, height: 32 })
          )}
        </IconButton>
      </Popover.Trigger>
      <Popover.Content size="2" width="300px" minHeight="100px">
        <label>
          <Heading as="h1" size="2" mb="1">
            {question.heading || (
              <Trans id="questions.Comment.response">Response</Trans>
            )}
          </Heading>
        </label>
        <Box mb="2">
          {answering ? (
            <>
              {question?.options?.sub_questions?.map((subQuestion, index) => (
                <SelectQuestion
                  key={index}
                  question={subQuestion}
                  subquestionResponse={subquestionResponse}
                  setSubquestionResponse={setSubquestionResponse}
                />
              ))}
              <Text as="p" size="2" mb="2">
                {question === null ? (
                  <Trans id="questions.Comment.loading">Loading</Trans>
                ) : (
                  question.sub_question
                )}
              </Text>
              <TextArea
                style={{ height: 80 }}
                placeholder={i18n._({
                  id: "questions.Comment.enter_comment",
                  message: "Enter your comment",
                })}
                onChange={handleCommentChange}
                defaultValue={comment}
              />
              {hoverFeature && (
                <Box pt="2">
                  <Text size="1" color="blue">
                    <Trans
                      id="Comment.on_feature"
                      variables={{
                        featureName: featureName,
                      }}
                    >
                      You are commenting on: {featureName}
                    </Trans>
                  </Text>
                </Box>
              )}
              {errorMessage && (
                <Text as="p" size="1" color="red" mt="1">
                  {errorMessage}
                </Text>
              )}
            </>
          ) : (
            <Flex gap="2" direction="column">
              <Text as="p" size="1" mb="2" color="blue">
                <Trans id="questions.Comment.your_answer">Your answer</Trans>
              </Text>
              {subquestionResponse
                ? question?.options?.sub_questions?.map(
                    (subQuestion, index) => (
                      <Flex gap="1" key={index}>
                        <Text as="p" size="1" color="gray">
                          {subQuestion.question}
                        </Text>
                        <Text as="p" size="1">
                          {subquestionResponse[subQuestion.question]}
                        </Text>
                      </Flex>
                    ),
                  )
                : null}
              <Text as="p" size="2">
                {formatComment(savedComment)}
              </Text>
            </Flex>
          )}
        </Box>
        <Flex gap="3" mt="4" justify="end">
          {answering ? (
            <>
              <Popover.Close>
                <Button
                  size="2"
                  variant="soft"
                  color="indigo"
                  onClick={handleCancelComment}
                >
                  <Trans id="questions.Comment.cancel">Cancel</Trans>
                </Button>
              </Popover.Close>
              <Popover.Close>
                <Button
                  disabled={
                    comment.length == 0 ||
                    errorMessage !== undefined ||
                    (comment == savedComment &&
                      JSON.stringify(subquestionResponse) ===
                        JSON.stringify(
                          response.data?.response?.subquestion_responses,
                        ))
                  }
                  onClick={handleSaveComment}
                  color="indigo"
                >
                  <Trans id="questions.Comment.save">Save</Trans>
                </Button>
              </Popover.Close>
            </>
          ) : (
            <>
              <Popover.Close>
                <Button
                  size="2"
                  variant="soft"
                  color="indigo"
                  onClick={handleCancelComment}
                >
                  <Trans id="questions.Comment.close">Close</Trans>
                </Button>
              </Popover.Close>
              <Popover.Close>
                <Button onClick={() => setAnswering(true)} color="indigo">
                  <Trans id="questions.Comment.edit">Edit</Trans>
                </Button>
              </Popover.Close>
            </>
          )}
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
}
